import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';
import { InstructionStepMaterials } from './InstructionStepMaterials';
import { stripLeadingStepNumberFromTitle } from './instructionStepUtils';
import type { InstructionStepDto, SupportingMaterialDto } from './types';

interface InstructionStepCarouselProps {
    steps: InstructionStepDto[];
    currentIndex: number;
    onIndexChange: (index: number) => void;
    supportingMaterials?: SupportingMaterialDto[];
    lessonTitle?: string;
    /** Full lesson step list for worksheet section slicing (not just the current session). */
    allLessonSteps?: InstructionStepDto[];
}

export function InstructionStepCarousel({
    steps,
    currentIndex,
    onIndexChange,
    supportingMaterials,
    lessonTitle,
    allLessonSteps
}: InstructionStepCarouselProps) {
    if (!steps.length) {
        return <p className="text-sm text-gray-500">No instruction steps available.</p>;
    }

    const safeIndex = Math.min(Math.max(currentIndex, 0), steps.length - 1);
    const step = steps[safeIndex];
    // Position within this session's steps (not absolute lesson step number).
    const sessionStepNumber = safeIndex + 1;
    const stepTitle =
        stripLeadingStepNumberFromTitle(step.e251_Summary) || `Step ${sessionStepNumber}`;

    return (
        <div>
            <div className="mb-8 flex items-center justify-between gap-2">
                <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={() => onIndexChange(Math.max(0, safeIndex - 1))}
                    disabled={safeIndex <= 0}
                    aria-label="Previous step"
                    className="shrink-0 bg-action-blue text-white hover:bg-action-blue-dark disabled:bg-action-blue/40"
                >
                    <ChevronLeft size={16} />
                </Button>
                <span className="min-w-0 text-center text-base font-semibold text-gray-900">
                    Step {sessionStepNumber} of {steps.length}
                </span>
                <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={() => onIndexChange(Math.min(steps.length - 1, safeIndex + 1))}
                    disabled={safeIndex >= steps.length - 1}
                    aria-label="Next step"
                    className="shrink-0 bg-action-blue text-white hover:bg-action-blue-dark disabled:bg-action-blue/40"
                >
                    <ChevronRight size={16} />
                </Button>
            </div>

            <div>
                <h6 className="mb-2 text-center text-base font-semibold text-gray-900">
                    {stepTitle}
                </h6>
                {step.e251_DetailedInstructions ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {step.e251_DetailedInstructions}
                    </p>
                ) : (
                    <p className="text-sm text-gray-500 italic">
                        Detailed guidance will appear when this lesson or activity is started.
                    </p>
                )}
                <InstructionStepMaterials
                    step={step}
                    supportingMaterials={supportingMaterials}
                    lessonTitle={lessonTitle}
                    lessonSteps={allLessonSteps ?? steps}
                />
            </div>
        </div>
    );
}
